/**
 * Custom branded toast component for FoneXpress
 * Uses react-toastify under the hood but with custom styling & icons
 */
import { toast as toastify, Slide } from 'react-toastify';

// ─── Toast Icon Components ──────────────────────────────────────────────────

const SuccessIcon = () => (
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </div>
);

const ErrorIcon = () => (
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 7L13 13M13 7L7 13" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
    </div>
);

const WarningIcon = () => (
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 7V11M10 13.5V13" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
    </div>
);

const InfoIcon = () => (
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 9V14M10 7V6.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
    </div>
);

// ─── Toast Body Component ───────────────────────────────────────────────────

function ToastBody({ icon, title, message, accentColor }) {
    return (
        <div className="flex items-start gap-3 py-0.5">
            {icon}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground leading-tight">
                    {title}
                </p>
                {message && (
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        {message}
                    </p>
                )}
            </div>
        </div>
    );
}

// ─── Toast Configuration ────────────────────────────────────────────────────

const TOAST_DEFAULTS = {
    position: 'top-right',
    autoClose: 3500,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    transition: Slide,
};

// ─── Toast Title helpers ────────────────────────────────────────────────────

function getSuccessTitle(msg) {
    if (!msg) return 'Success';
    // If message is short enough, use it as title, no body
    return 'Success';
}

function getErrorTitle(msg) {
    if (!msg) return 'Error';
    return 'Something went wrong';
}

// ─── Public Toast API ───────────────────────────────────────────────────────
// Drop-in replacement for react-toastify's toast.success / toast.error etc.

const toast = {
    success(message, options = {}) {
        return toastify(
            <ToastBody
                icon={<SuccessIcon />}
                title="Success"
                message={typeof message === 'string' ? message : undefined}
                accentColor="#10B981"
            />,
            {
                ...TOAST_DEFAULTS,
                className: 'fxp-toast fxp-toast--success',
                progressClassName: 'fxp-toast__progress--success',
                autoClose: 3000,
                ...options,
            }
        );
    },

    error(message, options = {}) {
        return toastify(
            <ToastBody
                icon={<ErrorIcon />}
                title="Error"
                message={typeof message === 'string' ? message : undefined}
                accentColor="#EF4444"
            />,
            {
                ...TOAST_DEFAULTS,
                className: 'fxp-toast fxp-toast--error',
                progressClassName: 'fxp-toast__progress--error',
                autoClose: 4500,
                ...options,
            }
        );
    },

    warning(message, options = {}) {
        return toastify(
            <ToastBody
                icon={<WarningIcon />}
                title="Warning"
                message={typeof message === 'string' ? message : undefined}
                accentColor="#F59E0B"
            />,
            {
                ...TOAST_DEFAULTS,
                className: 'fxp-toast fxp-toast--warning',
                progressClassName: 'fxp-toast__progress--warning',
                autoClose: 4000,
                ...options,
            }
        );
    },

    info(message, options = {}) {
        return toastify(
            <ToastBody
                icon={<InfoIcon />}
                title="Info"
                message={typeof message === 'string' ? message : undefined}
                accentColor="#3B82F6"
            />,
            {
                ...TOAST_DEFAULTS,
                className: 'fxp-toast fxp-toast--info',
                progressClassName: 'fxp-toast__progress--info',
                autoClose: 3500,
                ...options,
            }
        );
    },

    // Proxy for Promise-based toasts
    promise(promise, opts, toastOpts = {}) {
        return toastify.promise(promise, opts, {
            ...TOAST_DEFAULTS,
            className: 'fxp-toast',
            ...toastOpts,
        });
    },

    // Dismiss
    dismiss(id) {
        return toastify.dismiss(id);
    },
};

export { toast };
export default toast;
